"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { getUserRole, ROLES, isAdmin } from "@/utils/roleUtils";
import Dashboard from "../../../../public/images/icon/dashboard.svg";
import ActiveDonor from "../../../../public/images/icon/activeDonors.svg";
import DonorPatient from "../../../../public/images/icon/donorPatines.svg";
import Seemans from "../../../../public/images/icon/seemansDonor.svg";
import Help from "../../../../public/images/icon/help.svg";
import LogOut from "../../../../public/images/icon/logOut.svg";
import DonorRequest from "../../../../public/images/icon/donorRequest.svg";
import Alert from "../../../../public/images/icon/alert.svg";
import DonorHospital from "../../../../public/images/icon/donorHospital.svg";
import DonorHistory from "../../../../public/images/icon/donorhistory.svg";
import ConsentForms from "../../../../public/images/icon/consentForms.svg";
import AffidavitForm from "../../../../public/images/icon/affidavitForm.svg";
import Setting from "../../../../public/images/icon/setting.svg";
import AddDonor from "../../../../public/images/icon/addDonor.svg";

export default function Sidebar({
  isOpen,
  setIsOpen,
  isCollapsed,
  setIsCollapsed,
}) {
  const pathname = usePathname();
  const [role, setRole] = useState(null);
  const [adminStatus, setAdminStatus] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    y: 0,
  });

  useEffect(() => {
    setRole(getUserRole());
    setAdminStatus(isAdmin());
    setIsLoaded(true);
  }, []);

  const getNavItems = () => {
    // Admin & Sub Admin menu items with sections
    if (adminStatus || role === "sub_admin") {
      return [
        {
          section: "MAIN MENU",
          items: [
            // { name: "Home", href: "/home", icon: "/images/icon/home.svg" },
            {
              name: "Dashboard",
              href: "/dashboard",
              icon: Dashboard,
            },
            {
              name: "Active Donors",
              href: "/donors/active",
              icon: ActiveDonor,
            },
            {
              name: "Donors/Patients",
              href: "/donors/history",
              icon: DonorPatient,
            },
            {
              name: "Donors Requests",
              href: "/donors/requests",
              icon: DonorRequest,
            },
            {
              name: "Alerts",
              href: "/alerts",
              icon: Alert,
              hasNotifications: true,
            },
            {
              name: "Centres/Doctors",
              href: "/centres",
              icon: DonorHospital,
            },
            {
              name: "Staff Management",
              href: "/users",
              icon: DonorHistory,
            },
            {
              name: "Consent Forms",
              href: "/consent-forms",
              icon: ConsentForms,
            },
            {
              name: "Affidavit Form",
              href: "/affidavit-forms",
              icon: AffidavitForm,
            },
          ],
        },
        {
          section: "SEMEN",
          items: [
            {
              name: "Semen Storage",
              href: "/storage",
              icon: Seemans,
            },
          ],
        },
        {
          section: "TOOLS",
          items: [
            { name: "Help", href: "/help", icon: Help },
            {
              name: "Settings",
              href: "/profile",
              icon: Setting,
            },
            {
              name: "Logout",
              href: "/logout",
              icon: LogOut,
              onClick: handleLogout,
            },
          ],
        },
      ];
    }

    // Non-admin menu items (flat structure)
    const baseItems = [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: Dashboard,
      },
    ];

    if (role === ROLES.RECEPTIONIST) {
      return [
        {
          section: "MAIN MENU",
          items: [
            ...baseItems,
            {
              name: "Active Donors",
              href: "/donors/active",
              icon: ActiveDonor,
            },
            {
              name: "Alerts",
              href: "/alerts",
              icon: Alert,
              hasNotifications: true,
            },
          ],
        },
        {
          section: "TOOLS",
          items: [
            { name: "Help", href: "/help", icon: Help },
            {
              name: "Settings",
              href: "/profile",
              icon: Setting,
            },
            {
              name: "Logout",
              href: "/logout",
              icon: LogOut,
              onClick: handleLogout,
            },
          ],
        },
      ];
    }

    if (role === ROLES.LABORATORY) {
      return [
        {
          section: "MAIN MENU",
          items: [
            ...baseItems,
          ],
        },
        {
          section: "TOOLS",
          items: [
            { name: "Help", href: "/help", icon: Help },
            {
              name: "Settings",
              href: "/profile",
              icon: Setting,
            },
            {
              name: "Logout",
              href: "/logout",
              icon: LogOut,
              onClick: handleLogout,
            },
          ],
        },
      ];
    }

    if (role === ROLES.DOCTOR) {
      return [
        {
          section: "MAIN MENU",
          items: [
            ...baseItems,
            {
              name: "Follow-up",
              href: "/donors/active",
              icon: ActiveDonor,
            },
            {
              name: "Request Status",
              href: "/donors/requests",
              icon: DonorRequest,
            },
            {
              name: "Donors/Patients",
              href: "/donors/history",
              icon: DonorPatient,
            },
            {
              name: "Alloted Donors",
              href: "/donors/allotted",
              icon: DonorHistory,
            },
            {
              name: "Alerts",
              href: "/alerts",
              icon: Alert,
              hasNotifications: true,
            },
          ],
        },
        {
          section: "SEMEN",
          items: [
            {
              name: "Semen Storage",
              href: "/storage",
              icon: Seemans,
            },
          ],
        },
        {
          section: "TOOLS",
          items: [
            { name: "Help", href: "/help", icon: Help },
            {
              name: "Settings",
              href: "/profile",
              icon: Setting,
            },
            {
              name: "Logout",
              href: "/logout",
              icon: LogOut,
              onClick: handleLogout,
            },
          ],
        },
      ];
    }

    const donorItems = [
      {
        name: "Active Donors",
        href: "/donors/active",
        icon: ActiveDonor,
      },
      {
        name: "Donors History",
        href: "/donors/history",
        icon: DonorPatient,
      },
      {
        name: "Add Donor",
        href: "/donors/add",
        icon: AddDonor,
      },
    ];

    return [
      { section: "MAIN MENU", items: [...baseItems, ...donorItems] },
      {
        section: "SEMEN",
        items: [
          {
            name: "Semen Storage",
            href: "/storage",
            icon: Seemans,
          },
        ],
      },
      {
        section: "TOOLS",
        items: [
          { name: "Help", href: "/help", icon: Help },
          {
            name: "Settings",
            href: "/profile",
            icon: Setting,
          },
          {
            name: "Logout",
            href: "/logout",
            icon: LogOut,
            onClick: handleLogout,
          },
        ],
      },
    ];
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("rememberedPassword");
    // Clear all cookies
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    window.location.href = "/auth/sign-in";
  };

  const renderIcon = (icon, className) => {
    const src = typeof icon === "string" ? icon : icon?.src ?? icon;
    return <Image src={src} alt="" width={24} height={24} className={className} />;
  };

  const navItems = getNavItems();
  const isActive = (href) => {
    if (pathname === href) return true;
    // Handle sub-routes for specific menu items - be more specific to avoid conflicts
    if (
      href === "/donors/active" &&
      pathname.startsWith("/donors/") &&
      !pathname.startsWith("/donors/history") &&
      !pathname.startsWith("/donors/requests") &&
      !pathname.startsWith("/donors/allotted")
    )
      return true;
    if (
      href === "/donors/requests" &&
      (pathname.startsWith("/donors/requests/") ||
        pathname.startsWith("/donor-requests/"))
    )
      return true;
    if (href === "/donors/history" && pathname.startsWith("/donors/history/"))
      return true;
    if (href === "/donors/allotted" && pathname.startsWith("/donors/allotted/"))
      return true;
    if (href === "/donors/add" && pathname.startsWith("/donors/add/"))
      return true;
    if (href === "/storage" && pathname.startsWith("/storage/")) return true;
    if (href === "/alerts" && pathname.startsWith("/alerts/")) return true;
    if (href === "/centres" && pathname.startsWith("/centres/")) return true;
    if (href === "/users" && pathname.startsWith("/users/")) return true;
    if (href === "/consent-forms" && pathname.startsWith("/consent-forms/"))
      return true;
    if (href === "/affidavit-forms" && pathname.startsWith("/affidavit-forms/"))
      return true;
    if (href === "/dashboard" && pathname.startsWith("/dashboard/"))
      return true;
    if (href === "/help" && pathname.startsWith("/help/")) return true;
    if (href === "/profile" && pathname.startsWith("/settings/")) return true;
    if (href === "/profile" && pathname.startsWith("/profile/")) return true;
    return false;
  };

  // Don't render until role is loaded
  if (!isLoaded) {
    return (
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] lg:static lg:inset-0 lg:translate-x-0 lg:shadow-sm ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } ${isCollapsed ? "w-16" : "w-64"}`}
      >
        <div className="flex h-full flex-col">
          <div
            className={`flex h-20 items-center border-b border-purple-600/20 bg-[#15A79D] transition-[padding] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
              isCollapsed ? "justify-center px-2" : "justify-between px-6"
            }`}
          >
            <Link href="/dashboard" className="flex items-center">
              <Image
                src={
                  isCollapsed
                    ? "/images/icon/brandLogoOnly.svg"
                    : "/images/icon/brand.svg"
                }
                alt="Logo"
                width={isCollapsed ? 32 : 170}
                height={32}
                className="transition-all duration-200 ease-out"
              />
            </Link>
          </div>
          <div className="flex flex-1 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#15A79D]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] lg:static lg:inset-0 lg:translate-x-0 lg:shadow-sm ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } ${isCollapsed ? "lg:w-16" : "lg:w-64"} w-80 sm:w-64`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div
            className={`flex h-20 items-center border-b border-purple-600/20 bg-[#15A79D] transition-[padding] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
              isCollapsed && window.innerWidth >= 1024 ? "justify-center px-2" : "justify-between px-6"
            }`}
          >
            <Link href="/dashboard" className="flex items-center">
              <Image
                src={
                  isCollapsed && window.innerWidth >= 1024
                    ? "/images/icon/brandLogoOnly.svg"
                    : "/images/icon/brand.svg"
                }
                alt="Logo"
                width={isCollapsed && window.innerWidth >= 1024 ? 43 : 170}
                height={32}
                className="transition-all duration-200 ease-out"
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white lg:hidden"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-6 scrollbar-hide">
            <div className="space-y-4">
              {navItems.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {/* Section Label */}
                  {section.section && !(isCollapsed && window.innerWidth >= 1024) && (
                    <div className="mb-3 px-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {section.section}
                      </h3>
                    </div>
                  )}

                  {/* Section Items */}
                  <div className="space-y-1">
                    {section.items.map((item) => (
                      <div
                        key={item.name}
                        className="relative"
                        onMouseEnter={(e) => {
                          if (isCollapsed && window.innerWidth >= 1024) {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              visible: true,
                              text: item.name,
                              y: rect.top + rect.height / 2,
                            });
                          }
                        }}
                        onMouseLeave={() => {
                          setTooltip({ visible: false, text: "", y: 0 });
                        }}
                      >
                        {item.onClick ? (
                          <button
                            onClick={() => {
                              item.onClick();
                              setIsOpen(false);
                            }}
                            className={`flex w-full items-center rounded-lg text-sm font-medium text-gray-700 transition-all duration-300 hover:bg-gray-100 hover:text-gray-900 ${
                              isCollapsed && window.innerWidth >= 1024
                                ? "mx-1 justify-center p-3"
                                : "mx-2 gap-3 px-3 py-2.5"
                            }`}
                          >
                            <div className="flex-shrink-0">
                              {renderIcon(item.icon, "h-6 w-6 brightness-0 transition-all duration-300")}
                            </div>

                            <span
                              className={`overflow-hidden whitespace-nowrap transition-[width,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                                isCollapsed
                                  ? "w-0 opacity-0"
                                  : "w-auto opacity-100"
                              }`}
                            >
                              {item.name}
                            </span>
                          </button>
                        ) : (
                          <Link
                            href={item.href}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center rounded-lg text-sm font-medium transition-all duration-300 ${
                              isActive(item.href)
                                ? "bg-[#15A79D] text-white shadow-sm"
                                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            } ${
                              isCollapsed && window.innerWidth >= 1024
                                ? "mx-1 justify-center p-3"
                                : "mx-2 gap-3 px-3 py-2.5"
                            }`}
                          >
                            <div className="flex-shrink-0">
                              {renderIcon(
                                item.icon,
                                `h-6 w-6 transition-all duration-300 ${
                                  isActive(item.href)
                                    ? "brightness-0 invert"
                                    : "brightness-0"
                                }`
                              )}
                            </div>

                            <span
                              className={`overflow-hidden whitespace-nowrap transition-[width,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                                isCollapsed && window.innerWidth >= 1024
                                  ? "w-0 opacity-0"
                                  : "w-auto opacity-100"
                              }`}
                            >
                              {item.name}
                            </span>
                          </Link>
                        )}

                        {/* Clean Gradient Tooltip */}
                        {tooltip.visible && (
                          <div
                            className="fixed z-[9999] animate-in fade-in-0 slide-in-from-left-1 duration-150"
                            style={{
                              left: "70px",
                              top: tooltip.y,
                              transform: "translateY(-50%)",
                            }}
                          >
                            <div className="relative">
                              <div className="whitespace-nowrap rounded-md bg-[#013C38] px-3 py-2 text-sm font-medium text-white">
                                {tooltip.text}
                              </div>
                              <div className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1 -translate-y-1/2 rotate-45 bg-[#013C38]"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
